import { useMemo } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { Flex } from "../../components/Flex";
import { H3 } from "../../components/Heading";
import { Loader } from "../../components/Loader";
import { Paragraph } from "../../components/Paragraph";
import { Slider } from "../../components/Slider";
import { CheckStatusEnum } from "../../context/MandateStatusContext";
import { useIndividual } from "../../data/useIndividual";
import { useIndividualCheck } from "../../data/useIndividualCheck";
import {
  CheckContainer,
  CheckTable,
  CheckTableCell,
  CheckTableHeaderRow,
  CheckTitle,
  Section,
} from "./CheckStyledComponents";
import { ApprovalComment } from "./approvalComment";
import { CheckFields } from "./CheckFields";
import { CheckResults } from "./CheckResults";
import { IndividualChecksMap } from "./helper";
import {
  ChangesReviewManualAcceptReject,
  ReviewChangeGroup,
} from "./ChangesReviewManualAcceptReject";
import { capitalise } from "../../utils/string";

type AddressField = {
  key: string;
  label: string;
};

type AddressComparison = {
  id: string;
  addressType: string;
  changeType: "new" | "modified" | "unchanged";
  profileAddress: any;
  submittedAddress: any;
  changedFields: string[];
};

const addressFields: AddressField[] = [
  { key: "flat_appartment_subbuilding", label: "Flat / sub-building" },
  { key: "building_number", label: "Building number" },
  { key: "building_name", label: "Building name" },
  { key: "road_street", label: "Road / street" },
  { key: "district", label: "District" },
  { key: "town_city", label: "Town / city" },
  { key: "state_province_name", label: "State / province" },
  { key: "country_code", label: "Country code" },
  { key: "post_zip_code", label: "Postcode" },
];

const CompactTableCell = styled(CheckTableCell)`
  ${({ theme: { colors, space } }: any) => `
    padding: ${space[2]};
    border-bottom: 1px solid ${colors.gray[10]};
    background: transparent;
    color: ${colors.gray[60]};
    vertical-align: top;
  `}
`;

const ChangePill = styled.span<{ variant: AddressComparison["changeType"] }>`
  ${({ variant, theme: { colors, fontFamily, fontSizes, space } }: any) => {
    const isNew = variant === "new";
    const isModified = variant === "modified";

    return `
      display: inline-block;
      border-radius: 999px;
      padding: ${space[0]} ${space[1]};
      background: ${isNew || isModified ? "#FFF3D6" : colors.gray[10]};
      color: ${isNew || isModified ? "#8A5A00" : colors.gray[50]};
      font-family: ${fontFamily};
      font-size: ${fontSizes[1]};
      font-weight: 700;
      white-space: nowrap;
    `;
  }}
`;

const ResultPill = styled.span<{ changed: boolean }>`
  ${({ changed, theme: { colors, fontFamily, space } }: any) => `
    display: inline-block;
    border-radius: 999px;
    padding: ${space[0]} ${space[1]};
    background: ${changed ? "#FFF3D6" : colors.gray[10]};
    color: ${changed ? "#8A5A00" : colors.gray[50]};
    font-family: ${fontFamily};
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
  `}
`;

const getDisplayValue = (value: any) => {
  if (value === undefined || value === null || value === "") return "-";
  if (typeof value === "object") {
    return (
      value.name ??
      value.label ??
      value.status ??
      value.title ??
      value.code ??
      value.id ??
      "-"
    );
  }
  return String(value);
};

const normalizeValue = (value: any) =>
  getDisplayValue(value).toLowerCase().replace(/\s+/g, " ").trim();

const getAddressType = (address: any) =>
  getDisplayValue(
    address?.address_type ??
      address?.type ??
      address?._type ??
      address?.addressType ??
      address?.label,
  );

const getAddressValue = (address: any, key: string) => {
  if (!address) return "";
  if (key === "country_code") {
    return (
      address.country_code ??
      address.country_name ??
      address.country?.name ??
      address.country
    );
  }
  return address[key];
};

const addressFieldChanged = (
  profileAddress: any,
  submittedAddress: any,
  key: string,
) =>
  normalizeValue(getAddressValue(profileAddress, key)) !==
  normalizeValue(getAddressValue(submittedAddress, key));

const fieldChanged = (comparison: AddressComparison, key: string) => {
  if (comparison.changeType === "new") return true;
  if (comparison.changedFields.length > 0) {
    return comparison.changedFields.includes(key);
  }

  return addressFieldChanged(
    comparison.profileAddress,
    comparison.submittedAddress,
    key,
  );
};

const getInstanceAddressRows = (instance: any) => {
  const rows =
    instance?.address_update_check?.details ??
    instance?.address_update?.details ??
    instance?.address_comparisons ??
    instance?.address_changes ??
    instance?.addresses ??
    instance?.details ??
    instance?.results ??
    [];

  return Array.isArray(rows) ? rows : [];
};

const getProfileAddresses = (individual: any) =>
  [individual?.address, ...(individual?.previous_address || [])].filter(
    Boolean,
  );

const getAddressComparison = (row: any, index: number): AddressComparison => {
  const profileAddress =
    row?.profile_address ??
    row?.baseline_address ??
    row?.existing_address ??
    row?.old_address ??
    row?.before ??
    row?.profile ??
    row?.current_address ??
    row?.current ??
    null;

  const submittedAddress =
    row?.submitted_address ??
    row?.new_address ??
    row?.updated_address ??
    row?.after ??
    row?.submitted ??
    row?.address ??
    row;

  const rawChangeType = String(
    row?.change_type ?? row?.changeType ?? row?.status ?? "",
  ).toLowerCase();
  const changedFields = Array.isArray(row?.changed_fields)
    ? row.changed_fields
    : [];
  const hasProfileAddress = Boolean(profileAddress);
  const changedCount = addressFields.filter((field) =>
    addressFieldChanged(profileAddress, submittedAddress, field.key),
  ).length;

  const changeType: AddressComparison["changeType"] =
    rawChangeType.includes("new") || !hasProfileAddress
      ? "new"
      : rawChangeType.includes("modified") ||
        rawChangeType.includes("changed") ||
        changedCount > 0
      ? "modified"
      : "unchanged";

  return {
    id: String(row?.id ?? submittedAddress?.id ?? index),
    addressType:
      row?.address_type_label ??
      row?.address_type ??
      (getAddressType(submittedAddress) ||
        getAddressType(profileAddress) ||
        "Address"),
    changeType,
    profileAddress,
    submittedAddress,
    changedFields,
  };
};

const getAddressComparisons = (instance: any, individual: any) => {
  const instanceRows = getInstanceAddressRows(instance);

  if (instanceRows.length > 0) {
    return instanceRows.map(getAddressComparison);
  }

  return getProfileAddresses(individual).map(
    (address, index): AddressComparison => ({
      id: String(address?.id ?? index),
      addressType: getAddressType(address) || "Address",
      changeType: "unchanged",
      profileAddress: address,
      submittedAddress: address,
      changedFields: [],
    }),
  );
};

export const AddressUpdateCheck = (props: any) => {
  const { isOpen, onRequestClose, id, useSlider = true } = props;
  const params = useParams();

  const { data: individualCheckData, isLoading: isLoadingIndividualCheck } =
    useIndividualCheck(id);
  const { data: individualData } = useIndividual(params.id);

  const check = individualCheckData?.data;
  const instance: any = check?.instance;
  const individual = individualData?.data;
  const title = check?.title || IndividualChecksMap.address_update_check.title;

  const comparisons = useMemo(
    () => getAddressComparisons(instance, individual),
    [instance, individual],
  );
  const reviewChangeGroups: ReviewChangeGroup[] = useMemo(
    () =>
      comparisons
        .map((comparison) => ({
          title: comparison.addressType,
          changes: addressFields
            .filter((field) => fieldChanged(comparison, field.key))
            .map((field) => ({
              field: field.label,
              oldValue: getDisplayValue(
                getAddressValue(comparison.profileAddress, field.key),
              ),
              newValue: getDisplayValue(
                getAddressValue(comparison.submittedAddress, field.key),
              ),
            })),
        }))
        .filter((group) => group.changes.length > 0),
    [comparisons],
  );
  const reviewChanges = useMemo(
    () =>
      reviewChangeGroups.flatMap((group) =>
        group.changes.map((change) => ({
          ...change,
          field: `${group.title} - ${change.field}`,
        })),
      ),
    [reviewChangeGroups],
  );

  const content = (
    <CheckContainer>
      <Flex justifyContent="space-between" alignItems="flex-start">
        <CheckTitle>{title}</CheckTitle>

        {check?.status?.status === CheckStatusEnum.Review && (
          <ChangesReviewManualAcceptReject
            id={id}
            title={title}
            changes={reviewChanges}
            changeGroups={reviewChangeGroups}
            onSuccess={() => onRequestClose(false)}
          />
        )}
      </Flex>

      {check?.status?.status && <CheckResults status={check.status.status} />}

      {instance?.approval_comment && (
        <ApprovalComment
          data={instance.approval_comment}
          status={check?.status?.status}
        />
      )}

      <CheckFields
        individual_checkfield_result={check?.individual_checkfield_result}
      />

      <Section pb={3}>
        <H3 mb={2}>Addresses</H3>

        {comparisons.length === 0 ? (
          <Paragraph color="gray.60" fontSize={1}>
            No addresses were submitted for comparison.
          </Paragraph>
        ) : (
          comparisons.map((comparison) => (
            <Section key={comparison.id} px={0} pb={3}>
              <Flex justifyContent="space-between" alignItems="center" mb={2}>
                <H3 mb={0}>{comparison.addressType}</H3>
                <ChangePill variant={comparison.changeType}>
                  {capitalise(comparison.changeType)}
                </ChangePill>
              </Flex>

              <CheckTable>
                <thead>
                  <CheckTableHeaderRow>
                    <CheckTableCell>Field</CheckTableCell>
                    <CheckTableCell>Baseline</CheckTableCell>
                    <CheckTableCell>Submitted</CheckTableCell>
                    <CheckTableCell>Result</CheckTableCell>
                  </CheckTableHeaderRow>
                </thead>
                <tbody>
                  {addressFields.map((field) => {
                    const changed = fieldChanged(comparison, field.key);

                    return (
                      <tr key={field.key}>
                        <CompactTableCell>
                          <Paragraph fontWeight={600} my={0}>
                            {field.label}
                          </Paragraph>
                        </CompactTableCell>
                        <CompactTableCell>
                          {getDisplayValue(
                            getAddressValue(
                              comparison.profileAddress,
                              field.key,
                            ),
                          )}
                        </CompactTableCell>
                        <CompactTableCell>
                          {getDisplayValue(
                            getAddressValue(
                              comparison.submittedAddress,
                              field.key,
                            ),
                          )}
                        </CompactTableCell>
                        <CompactTableCell>
                          <ResultPill changed={changed}>
                            {changed ? "CHANGED" : "NO CHANGE"}
                          </ResultPill>
                        </CompactTableCell>
                      </tr>
                    );
                  })}
                </tbody>
              </CheckTable>
            </Section>
          ))
        )}
      </Section>
    </CheckContainer>
  );

  return (
    <>
      {isLoadingIndividualCheck ? (
        <Flex justifyContent="center" alignItems="center" minHeight="60vh">
          <Loader />
        </Flex>
      ) : useSlider ? (
        <Slider
          isOpen={isOpen}
          onRequestClose={() => onRequestClose(false)}
          width="70%"
        >
          {content}
        </Slider>
      ) : (
        content
      )}
    </>
  );
};
